class CollectionList {
  constructor(gridEl, cartBarEl) {
    this.gridEl = gridEl;
    this._items = {};

    // Per-product tier config: { handle: { tiers: [[minQty, percent], ...], unitLabel } }
    // Items are grouped by tier signature; each group is discounted independently.
    this._tiersConfig = JSON.parse(cartBarEl.dataset.tiersConfig || '{}');

    // Monthly subscription product data for smart devices
    const rawMembership = cartBarEl.dataset.smartMembership;
    this._smartMembership = rawMembership ? JSON.parse(rawMembership) : null;

    // Cart bar elements
    this._cartBar = cartBarEl;
    this._cartBarBtn = cartBarEl.querySelector('.tmd-cart-bar__btn');
    this._upsellEl = cartBarEl.querySelector('.tmd-cart-bar__upsell');
    this._btnContent = cartBarEl.querySelector('.tmd-cart-bar__btn-content');
    this._btnLabel = cartBarEl.querySelector('.tmd-cart-bar__btn-label');
    this._loader = cartBarEl.querySelector('.tmd-cart-bar__loader');
    this._totalAmountEl = cartBarEl.querySelector('.tmd-cart-bar__total-amount');
    this._savingsEl = cartBarEl.querySelector('.tmd-cart-bar__savings');
    this._priceOriginalEl = cartBarEl.querySelector('.tmd-cart-bar__price-original');
    this._savedEl = cartBarEl.querySelector('.tmd-cart-bar__saved');

    // .bar-container elements — cached with parent + nextSibling so we can
    // remove them from the DOM when the cart bar is active and put them back
    // in their exact original position when it's not.
    this._bars = Array.from(document.querySelectorAll('.bar-container')).map((node) => ({
      node,
      parent: node.parentNode,
      next: node.nextSibling,
    }));

    // Free gift options — read from global so a duplicated page can set it
    // without modifying the shared liquid snippet.
    const globalGifts = (typeof window !== 'undefined') ? window.TMD_FREE_GIFT_OPTIONS : null;
    this._freeGiftOptions = Array.isArray(globalGifts) && globalGifts.length ? globalGifts : null;

    // Optional discount coupon — applied via /discount/<code> before /cart/add
    const globalCoupon = (typeof window !== 'undefined') ? window.TMD_COUPON_CODE : null;
    this._couponCode = (typeof globalCoupon === 'string' && globalCoupon.trim()) ? globalCoupon.trim() : null;

    this._renderCardPrices();
    this._bindEvents();
  }

  _renderCardPrices() {
    this.gridEl.querySelectorAll('.tmd-cl-card').forEach((card) => {
      const priceEl = card.querySelector('.tmd-cl-card__price');
      if (priceEl) priceEl.textContent = this._formatMoney(parseInt(card.dataset.price, 10));
    });
  }

  _bindEvents() {
    this.gridEl.addEventListener('click', (e) => {
      const addBtn = e.target.closest('.tmd-cl-btn-add');
      const plusBtn = e.target.closest('.tmd-cl-qty__plus');
      const minusBtn = e.target.closest('.tmd-cl-qty__minus');

      if (addBtn) this._handleAdd(addBtn.closest('.tmd-cl-card'));
      if (plusBtn) this._handleQtyChange(plusBtn.closest('.tmd-cl-card'), 1);
      if (minusBtn) this._handleQtyChange(minusBtn.closest('.tmd-cl-card'), -1);
    });

    this._cartBarBtn.addEventListener('click', () => this._addToCart());
  }

  _handleAdd(card) {
    const variantId = card.dataset.variantId;
    const unitPrice = parseInt(card.dataset.price, 10);

    card.querySelector('.tmd-cl-btn-add').hidden = true;
    const qtyEl = card.querySelector('.tmd-cl-qty-selector');
    qtyEl.removeAttribute('aria-hidden');
    qtyEl.querySelector('.tmd-cl-qty__value').textContent = '1';
    card.classList.add('is-selected');

    this._items[variantId] = {
      variantId,
      productId: card.dataset.productId,
      title: card.dataset.title,
      handle: card.dataset.handle,
      quantity: 1,
      unitPrice,
      isSmart: card.dataset.isSmart === 'true',
    };
    this._updateCartBar();
  }

  _handleQtyChange(card, delta) {
    const variantId = card.dataset.variantId;
    const item = this._items[variantId];
    if (!item) return;

    const newQty = item.quantity + delta;
    if (newQty < 1) {
      this._revertToAdd(card, variantId);
      return;
    }

    item.quantity = newQty;
    card.querySelector('.tmd-cl-qty__value').textContent = newQty;
    this._updateCartBar();
  }

  _revertToAdd(card, variantId) {
    delete this._items[variantId];
    card.querySelector('.tmd-cl-btn-add').hidden = false;
    card.querySelector('.tmd-cl-qty-selector').setAttribute('aria-hidden', 'true');
    card.classList.remove('is-selected');
    this._updateCartBar();
  }

  _updateCartBar() {
    const items = Object.values(this._items);
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

    if (totalQty === 0) {
      this._cartBar.setAttribute('aria-hidden', 'true');
      this._toggleBars(false);
      return;
    }
    this._cartBar.removeAttribute('aria-hidden');
    this._toggleBars(true);

    // Group items by tier signature. Items whose handle has no config entry
    // fall into a "no-discount" group with empty tiers (rate 0, no upsell).
    const groups = {};
    for (const item of items) {
      const cfg = this._tiersConfig[item.handle];
      const tiers = cfg?.tiers || [];
      const sig = JSON.stringify(tiers);
      if (!groups[sig]) {
        groups[sig] = {
          tiers,
          unitLabel: cfg?.unitLabel || '',
          qty: 0,
          raw: 0,
        };
      }
      groups[sig].qty += item.quantity;
      groups[sig].raw += item.unitPrice * item.quantity;
    }

    let rawTotal = 0;
    let discountedTotal = 0;
    let bestUpsell = null; // { delta, label, percent }

    for (const g of Object.values(groups)) {
      // Tiers sorted asc by minQty so the last passed entry is the current rate
      // and the first not-yet-reached entry is the next upsell target.
      const sortedTiers = [...g.tiers].sort((a, b) => a[0] - b[0]);
      let currentPct = 0;
      let nextTier = null;
      for (const [minQty, pct] of sortedTiers) {
        if (g.qty >= minQty) currentPct = pct;
        else if (!nextTier) nextTier = { minQty, pct };
      }

      const groupDiscounted = Math.round(g.raw * (1 - currentPct / 100));
      rawTotal += g.raw;
      discountedTotal += groupDiscounted;

      if (nextTier && (!bestUpsell || nextTier.pct > bestUpsell.percent)) {
        bestUpsell = {
          delta: nextTier.minQty - g.qty,
          label: g.unitLabel,
          percent: nextTier.pct,
        };
      }
    }

    if (bestUpsell) {
      const noun = bestUpsell.delta === 1 ? bestUpsell.label : `${bestUpsell.label}s`;
      this._upsellEl.innerHTML =
        `Add ${bestUpsell.delta} more ${noun} to get <strong>${bestUpsell.percent}% OFF</strong>`;
      this._upsellEl.hidden = false;
    } else {
      this._upsellEl.hidden = true;
    }

    const saved = rawTotal - discountedTotal;
    this._totalAmountEl.textContent = this._formatMoney(discountedTotal);

    if (saved > 0) {
      this._savingsEl.hidden = false;
      this._priceOriginalEl.textContent = this._formatMoney(rawTotal);
      this._savedEl.textContent = 'Saved ' + this._formatMoney(saved) + '!';
    } else {
      this._savingsEl.hidden = true;
    }
  }

  _toggleBars(remove) {
    this._bars.forEach((entry) => {
      if (remove) {
        if (entry.node.parentNode) entry.node.parentNode.removeChild(entry.node);
      } else if (!entry.node.parentNode && entry.parent) {
        entry.parent.insertBefore(entry.node, entry.next);
      }
    });
  }

  _getSmartMembershipPlanId() {
    const product = this._smartMembership;
    if (!product?.selling_plan_groups) return null;
    const target = 'monthly smart membership';
    for (const group of product.selling_plan_groups) {
      const plan = group.selling_plans?.find((p) => p.name.toLowerCase().trim() === target);
      if (plan) return plan.id;
    }
    return null;
  }

  async _addToCart() {
    const giftProperties = this._freeGiftOptions
      ? { '_tmd-fg': JSON.stringify(this._freeGiftOptions) }
      : null;

    const items = Object.values(this._items).map((item) => {
      const line = {
        id: parseInt(item.variantId, 10),
        quantity: item.quantity,
      };
      if (giftProperties) line.properties = giftProperties;
      return line;
    });
    if (!items.length) return;

    // Prepend 1 monthly subscription if any smart product is selected
    const hasSmartProduct = Object.values(this._items).some((i) => i.isSmart);
    if (hasSmartProduct && this._smartMembership?.variants?.[0]) {
      const planId = this._getSmartMembershipPlanId();
      if (planId) {
        items.unshift({
          id: this._smartMembership.variants[0].id,
          quantity: 1,
          selling_plan: planId,
        });
      }
    }

    this._btnContent.hidden = true;
    this._loader.removeAttribute('aria-hidden');
    this._cartBarBtn.disabled = true;

    try {
      if (this._couponCode) {
        try {
          await fetch(`${window.location.origin}/discount/${encodeURIComponent(this._couponCode)}`);
        } catch (_err) {
          // Silent fail — proceed with cart add even if coupon request fails
        }
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Cart add failed');
    } catch (_err) {
      // Silent fail — button restores regardless
    } finally {
      this._btnContent.hidden = false;
      this._loader.setAttribute('aria-hidden', 'true');
      this._cartBarBtn.disabled = false;
    }
  }

  _formatMoney(cents) {
    const symbol = window.LPLocalizationSettings?.currency?.symbol ?? '$';
    return symbol + Math.round(cents / 100).toLocaleString();
  }
}

// ---------------------------------------------------------------------------

// Image slider — transform-based carousel with Pointer Events.
//
// The whole flex row (.tmd-cl-slider__track) is moved with translateX. The CSS
// transition animates the glide; while dragging we add .is-dragging to drop the
// transition so the row tracks the pointer 1:1, then re-enable it on release so
// it eases to the target. No native scroll / scroll-snap is used, so motion is
// identical and smooth across mouse, trackpad, and touch. One instance per
// .tmd-cl-slider.
class CardSlider {
  constructor(rootEl) {
    this.root = rootEl;
    this.track = rootEl.querySelector('[data-slider-track]');
    this.dots = Array.from(rootEl.querySelectorAll('.tmd-cl-slider__dot'));
    if (!this.track || this.dots.length === 0) return;

    this.index = 0;
    this.count = this.dots.length;
    this._drag = null;

    this._bindDots();
    this._bindDrag();
    this._render(); // position on slide 0
  }

  // Jump/animate to a slide. Resting position uses a percentage transform so it
  // stays correct across viewport resizes without recomputing pixels.
  goTo(index) {
    this.index = Math.max(0, Math.min(index, this.count - 1));
    this._render();
  }

  _render() {
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));

    // The last image has a dark background — flag the image area so overlay UI
    // (e.g. the HSA/FSA badge) can invert to white for contrast.
    const wrap = this.root.closest('.tmd-cl-card__image-wrap');
    if (wrap) wrap.classList.toggle('is-dark-slide', this.index === this.count - 1);
  }

  _bindDots() {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.slideIndex, 10) || 0));
    });
  }

  // Pointer drag — works for mouse and touch. touch-action:pan-y (CSS) lets the
  // page scroll vertically while we own horizontal movement. On release we
  // advance one slide if the drag passed a small threshold, otherwise snap back.
  _bindDrag() {
    const onDown = (e) => {
      // Ignore secondary/middle mouse buttons; touch/pen always pass.
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this._drag = { startX: e.clientX, width: this.track.clientWidth, moved: false };
      this.track.classList.add('is-dragging'); // drop the transition for 1:1 follow
      if (this.track.setPointerCapture) {
        try { this.track.setPointerCapture(e.pointerId); } catch (_err) { /* noop */ }
      }
    };

    const onMove = (e) => {
      if (!this._drag) return;
      const dx = e.clientX - this._drag.startX;
      if (Math.abs(dx) > 3) this._drag.moved = true;
      const basePx = -this.index * this._drag.width;
      this.track.style.transform = `translateX(${basePx + dx}px)`;
    };

    const onUp = (e) => {
      if (!this._drag) return;
      const dx = e.clientX - this._drag.startX;
      const threshold = this._drag.width * 0.15;

      let target = this.index;
      if (dx <= -threshold) target = this.index + 1;
      else if (dx >= threshold) target = this.index - 1;

      this.track.classList.remove('is-dragging'); // restore transition for the glide
      this._drag = null;
      this.goTo(target); // animates from the px transform to the % rest position
    };

    this.track.addEventListener('pointerdown', onDown);
    this.track.addEventListener('pointermove', onMove);
    this.track.addEventListener('pointerup', onUp);
    this.track.addEventListener('pointercancel', onUp);
  }
}

// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  const gridEl = document.querySelector('.tmd-collection-list');
  const cartBarEl = document.getElementById('tmd-cart-bar');
  if (gridEl && cartBarEl) new CollectionList(gridEl, cartBarEl);

  document.querySelectorAll('.tmd-cl-slider').forEach((el) => new CardSlider(el));
});